/**
 * S3: name your app. Step one of three.
 *
 * The only decision on this screen is a name, and the entire design job is
 * making it not feel like a decision. That is what "You can change this later."
 * does, and it is the most valuable string on the screen: it removes the
 * weight of the choice in five words, which is cheaper than any amount of
 * layout.
 *
 * The web address preview under the field is teaching, not decoration. Nobody
 * explains what a web address is anywhere in this flow. A person types "Herb
 * and Ash", watches herb-and-ash.apps.sv-academy.org appear underneath, and
 * now knows, without having been told, both what the address is and where it
 * comes from.
 *
 * INVALID CHARACTERS ARE NOT AN ERROR STATE AND NEVER WILL BE. Spaces become
 * hyphens, punctuation is dropped, capitals fold, all silently, and the
 * preview shows the result. Scolding someone for typing their project's name
 * the way they say it is the fastest way to teach them this product is not for
 * them.
 *
 * States, all reachable by typing, no deep links needed:
 *   empty      Continue sits flat
 *   checking   a neutral shimmer on the availability line
 *   available  a check and the word
 *   taken      the word and the alternative as a chip that applies itself
 *   reserved   type "api" or "www"
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Field } from '../../components/primitives/Field';
import { StepDots } from '../../components/patterns/StepDots';

import { useAccount } from '../../lib/auth/use-app-data';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { t } from '../../lib/i18n/t';
import { checkSubdomain } from '../../lib/real-api';
import { readDraft, writeDraft } from '../../lib/onboarding/draft';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { captureEvent } from '../../lib/analytics/posthog';
import {
  ONBOARDING_STEPS,
  PUBLIC_DOMAIN,
  slugify,
  suggestionFor,
  verdictFor,
} from '../../lib/mock/onboarding';
import type { NameVerdict } from '../../lib/mock/onboarding';

import '../../styles/screens/onboarding.css';
// The ADVANCED chip is one treatment in one place. This screen borrows the
// disclosure's rather than drawing a fifth copy of the same 20px box.
import '../../styles/components/disclosure.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** Screen spec S3 zone 5: the preview settles 200ms after the last keystroke. */
const DEBOUNCE_MS = 200;
/** And the shimmer holds for 400ms, so the line reads as a lookup, not a lag
 * one in mock mode; real mode's shimmer holds for however long checkSubdomain
 * actually takes. */
const CHECK_MS = 400;

/** checkSubdomain's {available, reason} answer, mapped onto the same
 * verdict vocabulary the mock's synchronous verdictFor uses, so the render
 * below doesn't need a second copy of the four-state display logic. */
function verdictFromReal(result: Awaited<ReturnType<typeof checkSubdomain>>): Exclude<NameVerdict, 'idle' | 'checking'> {
  if (result.available) return 'available';
  if (result.reason === 'reserved') return 'reserved';
  if (result.reason === 'taken') return 'taken';
  // 'invalid' (too short, bad characters) has no dedicated copy on this
  // screen, slugify() already keeps typed input inside the valid alphabet,
  // so the only way here is a name shorter than the 3-character minimum,
  // which reads fine as "reserved" 's generic guidance.
  return 'reserved';
}

/**
 * The example in the empty field, and the address the preview derives while the
 * field is still empty. One function, so the two can never disagree, and the
 * string is a literal inside t() rather than a constant read through
 * t(variable), which an extractor scanning for literals never sees.
 */
function placeholderName(): string {
  return t('My recipe site');
}

export default function NameApp() {
  const navigate = useNavigate();
  const [advanced] = useAdvanced();
  const { svgitEnabled } = useAccount();

  // Coming back from the next screen: keep what they typed.
  const [name, setName] = useState(() => {
    const saved = readDraft()?.name;
    return saved && saved !== placeholderName() ? saved : '';
  });
  /** Set only in Advanced, where the address stops following the name. */
  const [addressOverride, setAddressOverride] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<NameVerdict>('idle');
  const timers = useRef<number[]>([]);

  const formTracker = useFormTracker({
    formId: 'name-app',
    formName: 'Name your app',
    totalFields: advanced ? 2 : 1,
  });

  const slug = addressOverride === null ? slugify(name) : slugify(addressOverride);
  const shown = slug || slugify(placeholderName());

  useEffect(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];

    if (!slug) {
      setVerdict('idle');
      return;
    }

    if (USE_MOCK) {
      timers.current.push(
        window.setTimeout(() => setVerdict('checking'), DEBOUNCE_MS),
        window.setTimeout(() => {
          const v = verdictFor(slug);
          setVerdict(v);
          captureEvent('app_name_verdict', { slug, verdict: v, mode: 'mock' });
        }, DEBOUNCE_MS + CHECK_MS),
      );
      return () => timers.current.forEach((id) => window.clearTimeout(id));
    }

    let stale = false;
    const debounceId = window.setTimeout(() => {
      setVerdict('checking');
      checkSubdomain(slug)
        .then((result) => {
          if (!stale) {
            const v = verdictFromReal(result);
            setVerdict(v);
            captureEvent('app_name_verdict', { slug, verdict: v, mode: 'real' });
          }
        })
        .catch(() => {
          if (!stale) setVerdict('idle');
        });
    }, DEBOUNCE_MS);
    timers.current.push(debounceId);

    return () => {
      stale = true;
      timers.current.forEach((id) => window.clearTimeout(id));
    };
  }, [slug]);

  const suggestion = useMemo(
    () => (verdict === 'taken' ? suggestionFor(slug) : null),
    [verdict, slug],
  );

  function applySuggestion(next: string) {
    captureEvent('app_name_suggestion_applied', {
      original_slug: slug,
      chosen_suggestion: next,
    });
    if (addressOverride === null) {
      setName(next);
      formTracker.onFieldChange('name', next);
    } else {
      setAddressOverride(next);
      formTracker.onFieldChange('addressOverride', next);
    }
  }

  return (
    <div className="onb">
      <StepDots
        steps={ONBOARDING_STEPS.map((step) => t(step))}
        current={0}
        label={t('Setting up your project')}
      />

      <div className="onb__col">
        <header className="onb__head">
          <h1 className="onb__title">{t('Give your app a name')}</h1>
        </header>

        <form
          className="onb__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (verdict !== 'available') return;
            formTracker.trackSubmit({ name: name.trim() || placeholderName(), slug });
            writeDraft({ name: name.trim() || placeholderName(), slug, ...(readDraft()?.codeHost ? { codeHost: readDraft()?.codeHost } : {}) });
            // Without SV Git on this account there is nothing to choose between.
            navigate(svgitEnabled ? `/new/code?app=${slug}` : `/new/connect?app=${slug}`);
          }}
        >
          {/* The heading is the label, so the field's own label is in the
              accessible tree and not on the screen. Printing both would put
              the same question on the page twice. */}
          <div className="name__field">
            <Field
              label={t('Name your app')}
              placeholder={placeholderName()}
              value={name}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              onFocus={() => formTracker.onFieldFocus('name')}
              onBlur={() => formTracker.onFieldBlur('name')}
              onChange={(event) => {
                setName(event.target.value);
                formTracker.onFieldChange('name', event.target.value);
              }}
            />
          </div>

          {/* The derivation, visible. Dimmed until there is something real in
              the field, so the placeholder address never reads as a claim. */}
          <p className="name__addr mono" data-real={slug ? 'yes' : 'no'}>
            {`${shown}.${PUBLIC_DOMAIN}`}
          </p>

          <p className="name__verdict" data-verdict={verdict} role="status" aria-live="polite">
            {verdict === 'checking' ? (
              <>
                <span className="name__shimmer" aria-hidden="true" />
                <span className="name__word">{t('Checking')}</span>
              </>
            ) : null}

            {verdict === 'available' ? (
              <>
                <span className="name__glyph" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
                    <path
                      d="M3 8.5 6.5 12 13 4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="name__word">{t('Available')}</span>
              </>
            ) : null}

            {verdict === 'taken' ? (
              <>
                <span className="name__glyph" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="13" height="13" focusable="false">
                    <path
                      d="M8 2.4 15 14H1Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="name__word">{t('That one is taken, What about:')}</span>
                {suggestion ? (
                  <button
                    type="button"
                    className="name__chip mono"
                    onClick={() => applySuggestion(suggestion)}
                  >
                    {suggestion}
                  </button>
                ) : null}
              </>
            ) : null}

            {verdict === 'reserved' ? (
              <>
                <span className="name__glyph" aria-hidden="true">
                  <svg viewBox="0 0 16 16" width="13" height="13" focusable="false">
                    <path
                      d="M8 2.4 15 14H1Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="name__word">
                  {t("That address is reserved. Try one with your project's name in it.")}
                </span>
              </>
            ) : null}
          </p>

          {/* Advanced unwelds the address from the name. In Simple the two are
              one thing on purpose, because a person who has to keep them in
              agreement has been handed a job the product should be doing. */}
          {advanced ? (
            <div className="onb__advanced">
              <span className="disc__chip">{t('ADVANCED')}</span>
              <Field
                label={t('Web address')}
                mono
                value={addressOverride ?? slugify(name)}
                autoComplete="off"
                spellCheck={false}
                onFocus={() => formTracker.onFieldFocus('addressOverride')}
                onBlur={() => formTracker.onFieldBlur('addressOverride')}
                onChange={(event) => {
                  setAddressOverride(event.target.value);
                  formTracker.onFieldChange('addressOverride', event.target.value);
                }}
                help={t('In Simple mode this always follows the name of your app.')}
              />
            </div>
          ) : null}

          <div className="onb__actions">
            <Button variant="solid" type="submit" disabled={verdict !== 'available'}>
              {t('Continue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
